import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function HomePage({
  focusAreas,
  homeHighlights,
  blogsError,
  blogsLoading,
  sortedBlogs,
  getSnippet,
  openPost,
}) {
  const carouselTrackRefs = useRef({});
  const carouselCardRefs = useRef({});
  const [activeByCategory, setActiveByCategory] = useState({});

  const categorizedBlogs = useMemo(() => {
    const grouped = {};
    sortedBlogs.forEach((blog) => {
      const categories = Array.isArray(blog.categories) && blog.categories.length
        ? blog.categories
        : ["General"];
      categories.forEach((category) => {
        const key = String(category || "").trim();
        if (!key) return;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(blog);
      });
    });
    return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0]));
  }, [sortedBlogs]);

  const snapshotStats = useMemo(() => {
    const totalBlogs = sortedBlogs.length;
    const totalCategories = categorizedBlogs.length;
    const totalLikes = sortedBlogs.reduce(
      (sum, blog) => sum + (Number.isFinite(blog.likes) ? blog.likes : 0),
      0
    );
    return { totalBlogs, totalCategories, totalLikes };
  }, [sortedBlogs, categorizedBlogs]);

  const getCarouselPosts = useCallback((posts) => posts, []);

  const moveCategory = useCallback((category, delta) => {
    const posts = categorizedBlogs.find(([key]) => key === category)?.[1] || [];
    if (!posts.length) return;
    setActiveByCategory((current) => ({
      ...current,
      [category]: ((Number.isInteger(current[category]) ? current[category] : 0) + delta + posts.length) % posts.length,
    }));
  }, [categorizedBlogs]);

  const scrollCategory = useCallback(
    (category, direction = "next") => {
      moveCategory(category, direction === "prev" ? -1 : 1);
    },
    [moveCategory]
  );

  useEffect(() => {
    setActiveByCategory((current) => {
      const next = { ...current };
      categorizedBlogs.forEach(([category, posts]) => {
        if (!Number.isInteger(next[category])) {
          next[category] = 0;
        }
      });
      Object.keys(next).forEach((key) => {
        if (!categorizedBlogs.some(([category]) => category === key)) delete next[key];
      });
      return next;
    });
  }, [categorizedBlogs]);

  useEffect(() => {
    categorizedBlogs.forEach(([category, posts]) => {
      const track = carouselTrackRefs.current[category];
      if (!track || !posts.length) return;
      const activeIndex = Number.isInteger(activeByCategory[category]) ? activeByCategory[category] : 0;
      const card = carouselCardRefs.current[category]?.[activeIndex];
      if (!card) return;
      const centeredLeft = card.offsetLeft - (track.clientWidth - card.clientWidth) / 2;
      track.scrollTo({
        left: Math.max(0, centeredLeft),
        behavior: "smooth",
      });
    });
  }, [activeByCategory, categorizedBlogs]);

  const featureSections = [
    {
      title: "Designing For Clarity",
      text:
        "I prefer researching on solutions that serve their purpose of being fast, focused, and calm. Each component is shaped to make decisions easier for their specific users. I care about typography rhythm, spacing consistency, and visual hierarchy so users can absorb information quickly without friction.",
      icon: (
        <svg viewBox="0 0 180 120" role="img">
          <rect x="10" y="16" width="160" height="88" rx="16" fill="#174f79" opacity="0.95" />
          <rect x="28" y="36" width="58" height="12" rx="6" fill="#87caef" />
          <rect x="28" y="58" width="110" height="10" rx="5" fill="#d5ecfa" />
          <rect x="28" y="74" width="82" height="10" rx="5" fill="#d5ecfa" opacity="0.82" />
        </svg>
      ),
    },
    {
      title: "Shipping With Structure",
      text:
        "From rough ideas to polished release, I keep a consistent system for drafts, priorities, and quality checks that scales as projects grow. This gives me a repeatable process for faster execution while keeping technical quality stable across multiple features and timelines.",
      icon: (
        <svg viewBox="0 0 180 120" role="img">
          <circle cx="52" cy="60" r="32" fill="#2f84be" />
          <circle cx="118" cy="44" r="22" fill="#8bc6ea" />
          <rect x="84" y="72" width="72" height="26" rx="10" fill="#e0f2fe" />
          <path d="M26 60h52M118 44h30" stroke="#d9efff" strokeWidth="6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: "Learning In Public",
      text:
        "This blog documents my experiments in engineering and product craft. I write what I build, what breaks, and what improves over time. The goal is to make each article practical, transparent, and useful for anyone building real software products consistently.",
      icon: (
        <svg viewBox="0 0 180 120" role="img">
          <rect x="18" y="18" width="66" height="84" rx="12" fill="#255f90" />
          <rect x="96" y="18" width="66" height="84" rx="12" fill="#4ea3d7" />
          <rect x="33" y="34" width="36" height="8" rx="4" fill="#ddf1fe" />
          <rect x="111" y="34" width="36" height="8" rx="4" fill="#edf8ff" />
          <rect x="33" y="52" width="36" height="6" rx="3" fill="#ddf1fe" opacity="0.85" />
          <rect x="111" y="52" width="36" height="6" rx="3" fill="#edf8ff" opacity="0.85" />
        </svg>
      ),
    },
  ];

  return (
    <main className="main">
      <div className="side-doodles" aria-hidden="true">
        <svg className="side-doodle doodle-a" viewBox="0 0 42 42" role="img">
          <circle cx="21" cy="21" r="18" fill="#b9e1ff" />
          <circle cx="16" cy="17" r="2.5" fill="#2e7bb6" />
          <circle cx="26" cy="17" r="2.5" fill="#2e7bb6" />
          <path
            d="M14 25c2.8 3.8 11.2 3.8 14 0"
            stroke="#2e7bb6"
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
        <svg className="side-doodle doodle-b" viewBox="0 0 42 42" role="img">
          <rect x="6" y="6" width="30" height="30" rx="10" fill="#a5d0ff" />
          <path
            d="M13 14l16 0M13 21l11 0M13 28l8 0"
            stroke="#4764b4"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
        <svg className="side-doodle doodle-c" viewBox="0 0 42 42" role="img">
          <path
            d="M21 4l4.4 9 9.8 1.4-7.1 6.8 1.7 9.7-8.8-4.7-8.8 4.7 1.7-9.7-7.1-6.8 9.8-1.4z"
            fill="#ffd89d"
          />
        </svg>
      </div>

      <section className="hero">
        <p className="hero-kicker">A Reflective Journal</p>
        <h1>Hi! I am Aksh. I document my daily insights, experiences and outlooks.</h1>
        <p>
          Here I'll be uploading blogs/articles and recording my projects that I work on (aligned with my interests). I'll be focusing on creating
          systems and life-long learning. The posts here, render my decisions,
          execution quality, and the specific outcomes.
        </p>
        <ul className="hero-list">
          {homeHighlights.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="parallax-strip">
        <div className="parallax-content">
          <h2>Building in public, refining in private.</h2>
          <p>
            This space tracks how my ideas evolve into polished outcomes through drafts,
            past experiences, and deliberate iteration.
          </p>
        </div>
      </section>

      <section className="feature-band-wrap">
        {featureSections.map((item, index) => (
          <article
            key={item.title}
            className={`feature-band ${index % 2 === 1 ? "reverse" : ""}`}
          >
            <div className="feature-copy">
              <p>Highlight</p>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
            <div className="feature-art" aria-hidden="true">
              {item.icon}
            </div>
          </article>
        ))}
      </section>

      <section className="alt-strip-wrap">
        <article className="alt-strip">
          <div className="alt-strip-copy">
            <p>About</p>
            <h3>Calm systems, strong execution, and consistent learning.</h3>
            <p>
              I enjoy building things that feel fast, useful, and intentional. My
              personality is equal parts curious and disciplined: I like experimenting
              with new ideas, but I care deeply about finishing and shipping quality work.
            </p>
          </div>
          <div className="alt-strip-art tilt-left" aria-hidden="true">
            <svg viewBox="0 0 140 90" role="img">
              <rect x="8" y="8" width="124" height="74" rx="14" fill="#e3f3fe" />
              <rect x="22" y="25" width="46" height="8" rx="4" fill="#2f7fb8" />
              <rect x="22" y="41" width="94" height="7" rx="3.5" fill="#7dbfe5" />
              <rect x="22" y="55" width="68" height="7" rx="3.5" fill="#9ad0ee" />
            </svg>
          </div>
        </article>

        <article className="alt-strip reverse">
          <div className="alt-strip-copy">
            <p>Focus Areas</p>
            <h3>What I optimize in every project I publish.</h3>
            <ul className="alt-strip-list">
              {focusAreas.map((area) => (
                <li key={area.title}>
                  <strong>{area.title}:</strong> {area.detail}
                </li>
              ))}
            </ul>
          </div>
          <div className="alt-strip-art tilt-right" aria-hidden="true">
            <svg viewBox="0 0 140 90" role="img">
              <circle cx="45" cy="45" r="24" fill="#2f7fb8" />
              <circle cx="82" cy="35" r="17" fill="#7ec1e7" />
              <rect x="72" y="54" width="50" height="18" rx="8" fill="#dff2ff" />
            </svg>
          </div>
        </article>
      </section>

      <section className="home-snapshot" aria-label="Blog Snapshot">
        <div className="snapshot-glow snapshot-glow-a" aria-hidden="true" />
        <div className="snapshot-glow snapshot-glow-b" aria-hidden="true" />
        <div className="snapshot-head">
          <p>Live Snapshot</p>
          <h3>Portfolio Blog Pulse</h3>
        </div>
        <div className="snapshot-grid">
          <article className="snapshot-tile tile-blogs">
            <span className="snapshot-label">Blogs</span>
            <strong>{snapshotStats.totalBlogs}</strong>
          </article>
          <article className="snapshot-tile tile-categories">
            <span className="snapshot-label">Categories</span>
            <strong>{snapshotStats.totalCategories}</strong>
          </article>
          <article className="snapshot-tile tile-likes">
            <span className="snapshot-label">Likes</span>
            <strong>{snapshotStats.totalLikes}</strong>
          </article>
        </div>
      </section>

      {categorizedBlogs.length > 0 && (
        <section className="category-carousels">
          {categorizedBlogs.map(([category, posts]) => (
            <article className="category-section" key={category}>
              <div className="category-head">
                <div className="category-title-block">
                  <p>Category Spotlight</p>
                  <h3>{`#${category}`}</h3>
                </div>
                <span className="category-count">{`${posts.length} post${posts.length === 1 ? "" : "s"}`}</span>
              </div>
              <div className="carousel-shell">
                <button
                  type="button"
                  className="carousel-arrow left"
                  onClick={() => scrollCategory(category, "prev")}
                  aria-label={`Previous ${category} posts`}
                >
                  <svg viewBox="0 0 20 20" role="img" aria-hidden="true">
                    <path d="M12.9 3.9L6.8 10l6.1 6.1" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <div
                  className="carousel-track"
                  ref={(node) => {
                    if (node) {
                      carouselTrackRefs.current[category] = node;
                    } else {
                      delete carouselTrackRefs.current[category];
                    }
                  }}
                >
                  {getCarouselPosts(posts).map((blog, index) => (
                    <article
                      className={`carousel-card ${
                        index === (activeByCategory[category] ?? 0) ? "active" : ""
                      }`}
                      key={`${category}-${blog.id}-${index}`}
                      ref={(node) => {
                        if (!carouselCardRefs.current[category]) carouselCardRefs.current[category] = [];
                        carouselCardRefs.current[category][index] = node;
                      }}
                    >
                      {(blog.bannerImage || blog.images?.[0]) && (
                        <img
                          className="blog-cover"
                          src={blog.bannerImage || blog.images[0]}
                          alt={blog.title}
                          loading="lazy"
                          style={{
                            objectPosition: blog.bannerImage
                              ? `${blog.bannerFocusX ?? 50}% ${blog.bannerFocusY ?? 50}%`
                              : `${blog.imageFocusX ?? 50}% ${blog.imageFocusY ?? 50}%`,
                          }}
                        />
                      )}
                      <p className="blog-date">
                        {blog.updatedAt
                          ? `Updated ${new Date(blog.updatedAt).toLocaleDateString()}`
                          : `Published ${new Date(blog.createdAt).toLocaleDateString()}`}
                      </p>
                      <button className="blog-card-link" type="button" onClick={() => openPost(blog.id)}>
                        <h2>{blog.title}</h2>
                        <p>{getSnippet(blog.content)}</p>
                      </button>
                      <p className="blog-tags">
                        {(blog.categories?.length ? blog.categories : ["General"]).map((tag) => (
                          <span key={`${blog.id}-${tag}`} className="blog-tag">
                            #{tag}
                          </span>
                        ))}
                      </p>
                      <button className="btn-secondary read-btn" type="button" onClick={() => openPost(blog.id)}>
                        Read full post
                      </button>
                    </article>
                  ))}
                </div>
                <button
                  type="button"
                  className="carousel-arrow right"
                  onClick={() => scrollCategory(category)}
                  aria-label={`Next ${category} posts`}
                >
                  <svg viewBox="0 0 20 20" role="img" aria-hidden="true">
                    <path d="M7.1 3.9L13.2 10l-6.1 6.1" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </article>
          ))}
        </section>
      )}

      {blogsError && <p className="data-status error-text">{blogsError}</p>}
      {blogsLoading && <p className="data-status">Loading blogs...</p>}
    </main>
  );
}

export default HomePage;
